import { motion } from "framer-motion";

const CHAPTERS = [
  {
    n: "01",
    title: "Communities Deserve Visibility.",
    body: "Culture isn't built in one place anymore — it's built everywhere, all at once. Launchpad exists for the creators filming in their hometowns, for the stories that deserve global light, not local limits. This is where local energy becomes global presence.",
    img: "https://images.pexels.com/photos/34516462/pexels-photo-34516462.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Broadcast camera operator in a live studio",
  },
  {
    n: "02",
    title: "Creators Deserve Opportunity.",
    body: "Stories don't need permission anymore. Creators keep ownership of their work and earn through a transparent revenue-share on every broadcast. Upload once. Rewarded forever.",
    img: "/archive-02.webp",
    alt: "C. Miller, aka C-Murder — subject of the Launchpad TV exclusive documentary short, in a reflective double-exposure portrait",
    pos: "center 18%",
  },
  {
    n: "03",
    title: "Discovery Expands Imagination.",
    body: "Search by continent and experience content shaped by real communities around the world. Not trends. Not algorithms. Real places. Real people. Real culture.",
    img: "https://images.pexels.com/photos/7709678/pexels-photo-7709678.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Professional broadcast camera on tripod",
  },
];

const Chapter = ({ c, flip }) => (
  <motion.article
    initial={{ opacity: 0, y: 56 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
    className={`grid lg:grid-cols-12 gap-8 lg:gap-12 items-center ${flip ? "" : ""}`}
    data-testid={`manifesto-chapter-${c.n}`}
  >
    <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
      <motion.div
        initial={{ clipPath: "inset(0 100% 0 0)" }}
        whileInView={{ clipPath: "inset(0 0% 0 0)" }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
        className="relative overflow-hidden"
      >
        <img
          src={c.img}
          alt={c.alt}
          width={940}
          height={705}
          loading="lazy"
          decoding="async"
          style={c.pos ? { objectPosition: c.pos } : undefined}
          className="w-full aspect-[4/3] object-cover grayscale-[35%] contrast-125 hover:grayscale-0 hover:scale-105 transition-[filter,transform] duration-700"
        />
        <div className="absolute inset-0 scanlines pointer-events-none" />
        <div className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.25em] text-signal bg-obsidian/70 backdrop-blur px-2 py-1">
          ARCHIVE // {c.n}
        </div>
      </motion.div>
    </div>
    <div className={`lg:col-span-7 ${flip ? "lg:order-1 lg:text-right" : ""}`}>
      <div className="font-display font-extrabold text-6xl sm:text-7xl lg:text-8xl text-outline leading-none select-none">
        {c.n}
      </div>
      <h3 className="mt-4 font-head font-extrabold tracking-tight text-xl sm:text-2xl lg:text-3xl text-white">
        {c.title}
      </h3>
      <p className={`mt-4 text-sm sm:text-base text-slate-400 leading-relaxed max-w-xl ${flip ? "lg:ml-auto" : ""}`}>
        {c.body}
      </p>
    </div>
  </motion.article>
);

export default function Manifesto() {
  return (
    <section id="manifesto" className="relative py-24 lg:py-32 px-4 sm:px-8 lg:px-16" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 lg:mb-24"
        >
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-signal">
            The Declaration
          </span>
          <h2 className="mt-3 font-head font-extrabold tracking-tight text-base md:text-lg text-slate-300 max-w-xl leading-relaxed">
            Three laws for the next era of television — written for the people who make it.
          </h2>
        </motion.div>
        <div className="space-y-24 lg:space-y-32">
          {CHAPTERS.map((c, i) => (
            <Chapter key={c.n} c={c} flip={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
}
